import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, History as HistoryIcon, Eye } from 'lucide-react';
import { format } from 'date-fns';

function History() {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get('/api/history');
      setReadings(response.data.readings || []);
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setLoading(false);
    }
  };

  const viewReading = (reading) => {
    localStorage.setItem('analysisResults', JSON.stringify(reading.output));
    navigate('/results');
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <HistoryIcon size={40} color="#00D9FF" />
            Reading History
          </h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>View all your past numerology readings</p>
        </motion.div>

        {readings.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
            <HistoryIcon size={48} color="#9CA3AF" style={{ margin: '0 auto 1rem' }} />
            <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No readings yet</h3>
            <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>Start your first reading to see your numerology insights</p>
            <button
              onClick={() => navigate('/problem-selection')}
              className="btn btn-primary"
              data-testid="start-reading"
            >
              Start Reading
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {readings.map((reading, index) => (
              <motion.div
                key={reading._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', textTransform: 'capitalize' }}>
                      {reading.problem.replace(/-/g, ' ')} - Full Analysis
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '0.75rem' }}>
                      {format(new Date(reading.created_at), 'MMM dd, yyyy - hh:mm a')}
                    </p>
                    {reading.output && reading.output.birth_numbers && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span className="badge badge-primary">
                          Driver: {reading.output.birth_numbers.driver}
                        </span>
                        <span className="badge" style={{ background: 'rgba(124, 58, 237, 0.1)', color: '#7C3AED' }}>
                          Conductor: {reading.output.birth_numbers.conductor}
                        </span>
                        {reading.output.analysis && reading.output.analysis.has_emergencies && (
                          <span className="badge badge-warning">
                            Has Alerts
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => viewReading(reading)}
                    className="btn btn-secondary"
                    data-testid={`view-reading-${reading._id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Eye size={18} />
                    View
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default History;